import {CSSProperties, ReactElement} from "react";
import {BracketRound as BracketRoundType, TournamentTeam} from "@/competitions/tournaments/types";
import BracketMatch from "./BracketMatch";
import "./BracketRound.scss";

/*
 * Bracket geometry (single-elim). A match card is two 32px team rows + borders;
 * round-0 matches sit UNIT apart center-to-center, and each subsequent round's
 * spacing doubles so a match centers between the pair that feeds it.
 * UNIT is exported so BracketViewer can align the champion card on the final
 * match's center using the same scale.
 */
const MATCH_H = 66;
export const BRACKET_UNIT = 86;
const UNIT = BRACKET_UNIT;

/*
 * Exported so callers that forward a shared bag of round props derive it from
 * here rather than re-declaring the shape. GroupsKnockoutView kept its own copy
 * and it went stale the moment `tournamentId` was added — the spread then failed
 * to typecheck against the very props it was spreading into.
 */
export interface BracketRoundProps {
    tournamentId: string;
    round: BracketRoundType;
    /** Full team data for seeds, logos, etc. */
    teams: Record<string, TournamentTeam>;
    /** Legacy support - team ID to name mapping */
    teamNames?: Record<string, string>;
    onMatchClick?: (matchId: string) => void;
    /** The viewer's own team id — flows to matches for the YOU tag. */
    highlightTeamId?: string;
    /**
     * Round index (0 = first round). When set, the round uses deterministic
     * bracket spacing + elbow connectors instead of even distribution.
     */
    depth?: number;
    /** True for the last playable round — draws a straight line to the champion. */
    isFinal?: boolean;
    /** Cumulative match count before this round, so labels run A→…→O bracket-wide. */
    labelOffset?: number;
    /** Display options passed to matches */
    showSeeds?: boolean;
    showLogos?: boolean;
    showTime?: boolean;
    compact?: boolean;
}

/** Match label letters (A, B, … Z, AA …) so each match card carries an id chip. */
function matchLabel(index: number): string {
    let n = index;
    let out = "";
    do {
        out = String.fromCharCode(65 + n % 26) + out;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return out;
}

/**
 * Single round (column) in a bracket
 * Contains styled round header and vertically-spaced matches
 */
export default function BracketRound({
    tournamentId,
    round,
    teams,
    teamNames,
    onMatchClick,
    highlightTeamId,
    depth,
    isFinal,
    labelOffset = 0,
    showSeeds = true,
    showLogos = true,
    showTime = false,
    compact = false,
}: BracketRoundProps): ReactElement {
    const connected = depth != null;
    /*
     * Deterministic spacing: a depth-d match block occupies UNIT·2^d of height,
     * centered — so per-side margin and the connector half-height both follow 2^d.
     */
    const span = connected ? 2 ** depth : 1;
    const style = connected
        ? {
            "--rmargin": `${(UNIT * span - MATCH_H) / 2}px`,
            "--half": `${UNIT * span / 2}px`,
        } as CSSProperties
        : undefined;

    const cls = `bracket-round${
        compact ? " bracket-round--compact" : ""
    }${connected ? " bracket-round--connected" : ""
    }${isFinal ? " bracket-round--final" : ""}`;

    return (
        <div className={cls} style={style}>
            <div className="bracket-round__header">
                <h4 className="bracket-round__title">{round.name}</h4>
            </div>
            <div className="bracket-round__matches">
                {round.matches.map((match, i) =>
                    <BracketMatch
                        key={match.matchId}
                        tournamentId={tournamentId}
                        match={match}
                        teams={teams}
                        teamNames={teamNames}
                        onMatchClick={onMatchClick}
                        highlightTeamId={highlightTeamId}
                        label={matchLabel(labelOffset + i)}
                        showSeeds={showSeeds}
                        showLogos={showLogos}
                        showTime={showTime}
                        compact={compact}
                    />)}
            </div>
        </div>
    );
}
